import { supabase } from '../lib/supabase';

export interface GeofencePoint {
  lat: number;
  lng: number;
}

export interface GeofenceResource {
  id: string;
  resource_id: string;
  role: string;
  resources: {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
  };
}

export interface Geofence {
  id?: string;
  business_id: string;
  name: string;
  description?: string;
  polygon_points: GeofencePoint[];
  is_active?: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  geofence_resources?: GeofenceResource[];
}

class GeofenceService {
  private getEdgeFunctionUrl(): string {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL environment variable not found');
    }
    
    const cleanUrl = supabaseUrl.replace(/\/$/, '');
    const baseUrl = `${cleanUrl}/functions/v1/geofences`;
    
    console.log('🌐 GeofenceService Base URL:', baseUrl);
    return baseUrl;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    console.log('🎯 MAKEQUEST DEBUG - Starting request');
    const baseUrl = this.getEdgeFunctionUrl();
    const url = `${baseUrl}${endpoint}`;
    
    console.log('🔗 URL CONSTRUCTION DEBUG:');
    console.log('  baseUrl type:', typeof baseUrl);
    console.log('  baseUrl value:', baseUrl);
    console.log('  endpoint type:', typeof endpoint);  
    console.log('  endpoint value:', endpoint);
    console.log('  final url type:', typeof url);
    console.log('  final url value:', url);
    console.log('  url includes v1:', url.includes('/v1/'));
    console.log('  url includes geofences:', url.includes('geofences'));
    
    console.log('🌐 GeofenceService Request:', {
      method: options.method || 'GET',
      baseUrl: baseUrl,
      endpoint: endpoint,
      fullUrl: url,
      timestamp: new Date().toISOString()
    });

    const { data: { session } } = await supabase!.auth.getSession();
    const token = session?.access_token;

    console.log('🔐 Auth Info:', {
      hasSession: !!session,
      hasToken: !!token,
      tokenLength: token?.length || 0,
      userId: session?.user?.id || 'none',
      userEmail: session?.user?.email || 'none'
    });

    if (options.body) {
      console.log('📤 Request Body:', JSON.parse(options.body as string));
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    console.log('📥 Response Status:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      url: response.url
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ GeofenceService Error:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        errorText: errorText,
        timestamp: new Date().toISOString()
      });
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const responseData = await response.json();
    console.log('✅ Response Data:', responseData);

    return responseData;
  }

  // Get all geofences for a business
  async getGeofences(businessId: string): Promise<Geofence[]> {
    return this.makeRequest<Geofence[]>(`/${businessId}`);
  }

  // Create a new geofence
  async createGeofence(geofence: Omit<Geofence, 'id' | 'created_at' | 'updated_at'>): Promise<Geofence> {
    return this.makeRequest<Geofence>('', {
      method: 'POST',
      body: JSON.stringify(geofence),
    });
  }

  // Update an existing geofence
  async updateGeofence(
    geofenceId: string, 
    updates: Partial<Pick<Geofence, 'name' | 'description' | 'polygon_points' | 'is_active'>>
  ): Promise<Geofence> {
    return this.makeRequest<Geofence>(`/${geofenceId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Delete a geofence
  async deleteGeofence(geofenceId: string): Promise<void> {
    return this.makeRequest<void>(`/${geofenceId}`, {
      method: 'DELETE',
    });
  }

  // Toggle geofence active status
  async toggleGeofence(geofenceId: string, isActive: boolean): Promise<Geofence> {
    return this.updateGeofence(geofenceId, { is_active: isActive });
  }

  // Add resource to geofence
  async addResourceToGeofence(
    geofenceId: string, 
    resourceId: string, 
    businessId: string, 
    role: string = 'assigned'
  ): Promise<GeofenceResource> {
    console.log('🎯 Adding resource:', { geofenceId, resourceId, businessId, role });
    return this.makeRequest<GeofenceResource>(`/${geofenceId}/resources`, {
      method: 'POST',
      body: JSON.stringify({
        resource_id: resourceId,
        business_id: businessId,
        role: role
      }),
    });
  }

  // Remove resource from geofence
  async removeResourceFromGeofence(geofenceId: string, resourceId: string): Promise<void> {
    console.log('🗑️ Removing resource:', { geofenceId, resourceId });
    return this.makeRequest<void>(`/${geofenceId}/resources/${resourceId}`, {
      method: 'DELETE',
    });
  }
}

// Export a singleton instance
export const geofenceService = new GeofenceService();
export default GeofenceService; 